/*
 * lazyWithRetry.js — WP-SPLIT
 *
 * React.lazy, but it does not strand anybody after a deploy.
 *
 * Every screen is now a separate file ("chunk") with the build's hash in its
 * name. A page that has been left open since before a deploy still asks for the
 * OLD hash, and that file no longer exists on the server, so the browser throws
 * a ChunkLoadError and React shows the error page. That is a horrible thing to
 * happen to somebody who only clicked a menu item.
 *
 * So: the first time a chunk fails to load in this browser tab we reload the
 * page once. The reload fetches the new index.html, which points at the new
 * chunk names, and the click works. The flag lives in sessionStorage, so one
 * tab gets exactly one automatic reload — never a reload loop if the file is
 * genuinely missing. Anything that is not a chunk-loading failure is rethrown
 * untouched so the error boundary still sees real bugs.
 *
 * Belt and braces: index.js already reloads the page when a new service worker
 * takes over (the service-worker hotfix, 17/09/2026), so most stale pages are
 * refreshed before they ever ask for a dead chunk. This catches the rest.
 */
import { lazy } from 'react';

// One tab, one automatic reload.
export const CHUNK_RELOAD_KEY = 'mm_chunk_reload_attempted';

/** True for the "the file I asked for isn't there" family of errors. */
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');
  return (
    name === 'ChunkLoadError'
    || /Loading chunk\s+\S+\s+failed/i.test(message)
    || /Loading CSS chunk/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
  );
}

function alreadyReloaded() {
  try {
    return Boolean(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
  } catch (e) {
    // Private browsing or a locked-down browser: treat it as "already tried",
    // so we never reload in a loop we cannot remember having started.
    return true;
  }
}

function rememberReload() {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch (e) {
    /* nothing we can do; the guard above already fails safe */
  }
}

/**
 * Wrap a `() => import('./Something')` factory the way React.lazy does, with
 * the one-reload rescue described above.
 */
export default function lazyWithRetry(factory) {
  return lazy(() => factory().catch((error) => {
    if (isChunkLoadError(error) && !alreadyReloaded()) {
      rememberReload();
      window.location.reload();
      // The page is going away. Never settle, so React does not flash an error
      // page in the fraction of a second before the reload happens.
      return new Promise(() => {});
    }
    throw error;
  }));
}
